// @ts-ignore
import { request } from '@umijs/max';
import { paramsSerializer } from './api';
import { registerWsHandler, WsEventType, onSocketReconnected } from './websocket';

export type AppNotification = {
  id: number;
  title: string;
  body: string;
  data: Record<string, any> | null;
  dispatch_id: number | null;
  notification_type: string;
  created_at: string;
  sent_at: string | null;
  read_at: string | null;
  is_read: 0 | 1;
};

export type NotificationListResult = {
  notifications: AppNotification[];
  pagination: {
    total: number;
    page: number;
    per_page: number;
  };
};

export const NOTIFICATION_TYPE_OPTIONS = [
  { label: 'Incoming SMS', value: 'incoming_sms' },
  { label: 'Dispatch Update', value: 'dispatch_update' },
  { label: 'Route Updated', value: 'route_change' },
  { label: 'Appointment Updated', value: 'appointment_change' },
  { label: 'Technician Reassigned', value: 'tech_reassign' },
  { label: 'Route Passcode Request', value: 'route_passcode_request' },
  { label: 'Survey Compliance Alert', value: 'survey_compliance_alert' },
  { label: 'Timecard Correction Reviewed', value: 'timecard_correction_reviewed' },
  { label: "Driver's License Expiry", value: 'driver_license_expiry' },
  { label: 'Social Post Approval Request', value: 'social_post_approval_request' },
  { label: 'Social Post Approved / Denied', value: 'social_post_approval_reviewed' },
  { label: 'Callback Due Reminder', value: 'callback_due' },
  { label: 'Time Off Request', value: 'time_off_request' },
  { label: 'Time Off Approved / Declined', value: 'time_off_reviewed' },
];

export const NOTIFICATIONS_UPDATED_EVENT = 'app:notifications-updated';

/**
 * Where a notifications-updated signal came from: 'push' = the server's WS push
 * (or the reconnect resync), which every open tab of the tenant receives at the
 * same moment; 'local' = this tab's own action (mark read, mark all, …).
 * Listeners can spread their re-fetch for pushes (useJitteredReload) and still
 * answer the user's own clicks instantly.
 */
export type NotificationsUpdatedSource = 'local' | 'push';

export function emitNotificationsUpdated(source: NotificationsUpdatedSource = 'local') {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<NotificationsUpdatedSource>(NOTIFICATIONS_UPDATED_EVENT, { detail: source }));
  }
}

export function onNotificationsUpdated(handler: (source: NotificationsUpdatedSource) => void) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const listener = (e: Event) => handler((e as CustomEvent<NotificationsUpdatedSource>).detail ?? 'local');
  window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, listener);
  return () => window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, listener);
}

// One tenant action can queue several notification rows (e.g. NotificationQueueHelper
// called once per affected technician), each firing its own WS push — coalesce a burst
// into a single re-fetch instead of one per message. Safe here since this push is
// signal-only (no data) and every listener just re-fetches over REST anyway.
registerWsHandler(WsEventType.NOTIFICATIONS_UPDATED, () => emitNotificationsUpdated('push'), { coalesceMs: 300 });

// A dropped push during a disconnect never arrives on its own — resync the
// instant the socket comes back instead of waiting out the ~30s poll fallback.
onSocketReconnected(() => emitNotificationsUpdated('push'));

/** GET /api/notifications — paginated list with is_read flag; `type` filters on data.type */
export async function getNotifications(params?: {
  page?: number;
  perPage?: number;
  type?: string;
}): Promise<NotificationListResult> {
  return request<{ success: boolean; data: NotificationListResult }>('/api/notifications', {
    method: 'GET',
    params,
    withToken: true,
  }).then((res) => res.data);
}

export type UnreadCountResult = {
  count: number;
  /** Number of unread notifications whose data.type is 'incoming_sms' */
  sms_count: number;
};

/** GET /api/notifications/unread-count — lightweight badge counts */
export async function getUnreadCount(): Promise<UnreadCountResult> {
  return request<{ success: boolean; data: UnreadCountResult }>(
    '/api/notifications/unread-count',
    { method: 'GET', withToken: true },
  ).then((res) => ({ count: res.data?.count ?? 0, sms_count: res.data?.sms_count ?? 0 }));
}

export type UnreadDispatchSummary = {
  ids: number[];
  /** dispatch_id → customer phone (E.164) of the unread conversation */
  phones: Record<number, string>;
};

/** GET /api/notifications/unread-dispatch-ids — dispatch ids with unread notifications (EDB red dots) */
export async function getUnreadDispatchIds(): Promise<UnreadDispatchSummary> {
  return request<{ success: boolean; data: { dispatch_ids: number[]; phones?: Record<string, string> } }>(
    '/api/notifications/unread-dispatch-ids',
    { method: 'GET', withToken: true },
  ).then((res) => ({
    ids: (res.data?.dispatch_ids ?? []).map(Number),
    phones: Object.fromEntries(
      Object.entries(res.data?.phones ?? {}).map(([id, phone]) => [Number(id), String(phone)]),
    ),
  }));
}

/** POST /api/notifications/{id}/mark-read */
export async function markNotificationRead(id: number): Promise<void> {
  await request(`/api/notifications/${id}/mark-read`, {
    method: 'POST',
    withToken: true,
  });
}

/** POST /api/notifications/mark-all-read */
export async function markAllNotificationsRead(): Promise<void> {
  await request('/api/notifications/mark-all-read', {
    method: 'POST',
    withToken: true,
  });
}

/**
 * POST /api/notifications/mark-read-bulk — mark a selected batch as read.
 * Scoped server-side to the caller's own subscriber rows, so ids for
 * notifications the viewer doesn't personally subscribe to (e.g. a
 * technician_mobile row shown for visibility only) are silently ignored.
 */
export async function markNotificationsReadBulk(ids: number[]): Promise<number> {
  return request<{ success: boolean; data: { updated_count: number } }>('/api/notifications/mark-read-bulk', {
    method: 'POST',
    data: { ids },
    withToken: true,
  }).then((res) => res.data?.updated_count ?? 0);
}

/**
 * GET /api/notifications/all — paginated queue-level view of notification_queue
 * events for the tenant (any channel: tenant_web or technician_mobile; any
 * status), one row per event with subscriber/read/unread counts aggregated
 * across recipients. `only_me` (default true) restricts to events the caller
 * is a subscriber of; turning it off requires the NOTIFICATION_VIEW_ALL
 * permission (enforced server-side — the toggle is hidden client-side without it).
 */
export async function getAllNotifications(params?: {
  current?: number;
  pageSize?: number;
  type?: string;
  notification_type?: 'tenant_web' | 'technician_mobile';
  status?: 'pending' | 'sent' | 'failed';
  date_from?: string;
  date_to?: string;
  dispatch_id?: number;
  /** Restrict to events the current user is a subscriber of. Defaults true server-side. */
  only_me?: boolean;
  /** Filter by the viewer's own read state (Gmail-style "is:unread"). */
  read_status?: 'read' | 'unread';
  /** Restrict to events this user is a subscriber of. Requires NOTIFICATION_VIEW_ALL. */
  subscriber_id?: number;
}): Promise<{ data: NotificationSummaryItem[]; success: boolean; total: number }> {
  return request<{ success: boolean; data: NotificationListResult }>('/api/notifications/all', {
    method: 'GET',
    params: {
      ...params,
      page: params?.current,
      perPage: params?.pageSize,
      current: undefined,
      pageSize: undefined,
    },
    withToken: true,
  }).then((res) => ({
    data: (res.data as any)?.notifications ?? [],
    success: res.success ?? false,
    total: res.data?.pagination?.total ?? 0,
  }));
}

export type NotificationReadLogItem = {
  id: number;
  notification_queue_id: number;
  user_id: number;
  read_at: string;
  marked_read_by_user_id: number | null;
  title: string;
  body: string;
  notified_at: string;
  recipient_name: string;
  marked_by_name: string | null;
};

/** GET /api/notifications/read-log/actors — distinct users who have marked notifications as read */
export async function getNotificationReadLogActors(): Promise<{ id: number; user_name: string }[]> {
  return request<{ success: boolean; data: { id: number; user_name: string }[] }>(
    '/api/notifications/read-log/actors',
    { method: 'GET', withToken: true },
  ).then((res) => res.data ?? []);
}

/** GET /api/notifications/read-log — paginated audit log of read events */
export async function getNotificationReadLog(params?: {
  current?: number;
  pageSize?: number;
  title?: string;
  marked_by_ids?: number[];
}): Promise<{ data: NotificationReadLogItem[]; success: boolean; total: number }> {
  return request<{ success: boolean; data: { rows: NotificationReadLogItem[]; pagination: { total: number } } }>(
    '/api/notifications/read-log',
    {
      method: 'GET',
      params: {
        ...params,
        page: params?.current,
        perPage: params?.pageSize,
      },
      withToken: true,
      paramsSerializer,
    },
  ).then((res) => ({
    data: res.data?.rows ?? [],
    success: res.success ?? false,
    total: res.data?.pagination?.total ?? 0,
  }));
}

export type NotificationSummaryItem = {
  id: number;
  title: string;
  body: string;
  data: Record<string, any> | null;
  dispatch_id: number | null;
  notification_type: 'tenant_web' | 'technician_mobile';
  status: 'pending' | 'sent' | 'failed';
  created_at: string;
  sent_at: string | null;
  subscriber_count: number;
  read_count: number;
  unread_count: number;
  /** The lone subscriber's name — only set when subscriber_count is 1. */
  subscriber_name: string | null;
  /** The current viewer's own read state. NULL when they aren't a personal
   *  subscriber of this row (e.g. a technician_mobile row shown for tenant-wide
   *  visibility only) — such rows can't be marked read from this page. */
  my_read_at: string | null;
};

export type NotificationRecipient = {
  id: number;
  user_id: number;
  read_at: string | null;
  recipient_name: string;
  profile_image: string | null;
  marked_by_name: string | null;
  user_type: 'tenant' | 'employee' | null;
  role: string | null;
};

/** GET /api/notifications/{id}/recipients — full read/unread breakdown for one notification_queue event */
export async function getNotificationRecipients(id: number): Promise<NotificationRecipient[]> {
  return request<{ success: boolean; data: { recipients: NotificationRecipient[] } }>(
    `/api/notifications/${id}/recipients`,
    { method: 'GET', withToken: true },
  ).then((res) => res.data?.recipients ?? []);
}
